import type { GrayWhaleState } from './types';
import { civilFromEpochDay, epochDayFromCivil } from './calendar';
import { held } from './held';
import { YEAR_DAYS, dailyDeath, type PopulationDraws } from './population';

/**
 * The eastern North Pacific gray whale (M112): the whole population, one
 * stock, which feeds in the Bering and Chukchi seas and calves off Baja
 * California: the game's fourth population of no sanctuary, between the
 * North Pacific's two bands and the Arctic. Counted by sex and year class
 * since birth, each class aged on the calving season's first day, 26
 * December; its calves born through the season to the mature females at the
 * pregnancy rate NOAA's model sets by the population two years before; every
 * whale dying at its age's rate, and the whales of five and over hunted off
 * Chukotka. Its draws are the caller's, as the right whales' are: binomial
 * deaths and Poisson calves on its own stream in the game, expected values in
 * its table (`scripts/graywhale.ts`).
 *
 * **Its rates are NOAA's model's**: Punt and Wade 2010 (NOAA Tech. Memo.
 * NMFS-AFSC-207, "Population status of the eastern North Pacific stock of
 * gray whales in 2009"), an age- and sex-structured model of the stock fitted
 * to its counts since 1967 and its catches since 1930, its baseline
 * posterior means: survival, carrying capacity, and the maximum sustainable
 * yield's rate and level, from which the pregnancy rate's dependence on the
 * population (its resilience and compensation, the two numbers worked out by
 * `scripts/graywhale.ts`) follows by the model's own equations. A world opens
 * at NOAA's count of the 2025/2026 migration, below the carrying capacity
 * after the die-off of 2019 to 2023, and the stock grows back toward it.
 *
 * **Where it is** (`grayWhaleCounts`, a view): south of 40°N, off California
 * and in Baja California's lagoons, between the southbound migration past
 * central California and the northbound's two phases, the mothers and their
 * calves last; north of it the rest of the year, on the way and on the
 * feeding grounds of the Bering and Chukchi seas, both sides of the Bering
 * Strait (the North Pacific's high band and the Arctic), how many on each
 * side unmeasured (open).
 */

/** A window of days in the year, [month, day] to [month, day], both in it; one that ends before it begins runs into the next year. */
export interface DayWindow {
  from: readonly [number, number];
  to: readonly [number, number];
}

/** A gray whale's life, each figure with its source. */
export interface GrayWhaleLife {
  id: 'eastern_north_pacific_gray';
  /** Survival a year: a calf's, over its first year; every older whale's, either sex (no catastrophe, no hunt). */
  calfSurvival: number;
  survival: number;
  /** The age of maturity, years: a female first calves the year after it (the model's first parturition, a_m + 1). */
  matureYears: number;
  /** The oldest class, years: it holds every whale of that age and over. */
  oldestYears: number;
  /** The carrying capacity in whales of one and over, and the maximum sustainable yield's rate and level (of one and over): the pregnancy rate's dependence on the population follows from them. */
  carryingCapacity: number;
  msyRate: number;
  msyLevel: number;
  /** The share of calves female. */
  femaleShare: number;
  /** The hunt off Chukotka: the whales struck a year, and the age they are hunted from. */
  huntedFromYears: number;
  huntPerYear: number;
  /** The calving season: the classes age on its first day. */
  calving: DayWindow;
  /** The southbound migration past central California, and the northbound's two phases: the other whales first, the mothers with calves last. */
  southbound: DayWindow;
  northbound: DayWindow;
  northboundMothers: DayWindow;
  /** NOAA's count of the whales in the migration before a world opens (the season whose calving opens in December of `season`): the opening's whales of one and over, and the two seasons its pregnancy rate looks back on. */
  count: { season: number; whales: number };
}

/**
 * The eastern North Pacific gray whale, *Eschrichtius robustus*, each figure
 * with its source as read (M112).
 */
export const EASTERN_NORTH_PACIFIC_GRAY_WHALE: GrayWhaleLife = {
  id: 'eastern_north_pacific_gray',
  /**
   * Punt and Wade 2010, Table 4 (p. 37), "Posterior distributions for the key
   * model outputs (posterior mean, posterior median [in square parenthesis],
   * and posterior 90% intervals) for the baseline analysis": "S0 0.711
   * [0.732] (0.423 0.950)", the calves' survival, "S1+ 0.981 [0.982] (0.957
   * 0.997)", every older whale's; "S_a is the annual survival rate of
   * animals of age a in the absence of catastrophic mortality events
   * (assumed to be the same for males and females)" (p. 6). In the die-offs
   * of 1999 and 2000 it fell to 0.847 (its catastrophe's 0.863; p. 14), in
   * 2019 to 2022 by about 10% a year (Punt et al. 2025, NMFS-SWFSC-720, p.
   * 2): not drawn (open, the game's events).
   */
  calfSurvival: 0.711,
  survival: 0.981,
  /**
   * "Rice and Wolman (1971) ... corrected by Rice (1990) for the
   * underestimation of whale ages by one year in the original study,
   * resulting in a median age of 9, and lower and upper bounds of 6 and 12"
   * (Punt and Wade 2010, p. 13), the age of maturity, the model's prior
   * U[6,12] (Table 3, p. 36); "P^M_t is the number of females that have
   * reached the age at first parturition by the start of year t", the sum
   * from a_m + 1 (Equation 2, p. 6): first calving at 10. A knife edge
   * (open: the spread of 6 to 12; one eastern whale known to have first
   * calved at seven, ibid., p. 13).
   */
  matureYears: 9,
  /**
   * The model's oldest class, "x is the maximum age class, which for
   * convenience is lumped across older age classes" (p. 6), its age not
   * given; Reilly 1984's Leslie matrix: "k + 1 specifies the number of age
   * classes. Here k + 1 was set at 40, since approximately 98% of the
   * population is 40 or younger" (Rep. int. Whal. Commn Special Issue 6, p.
   * 391): the game's 41 classes, 0 to 40 and over (one survival for every
   * whale of one and over, and one hunt for every whale of five and over,
   * where the classes end moves nothing but their count). The oldest aged
   * were a female of 76 to 77 and a male of 69 (ibid., Table 5, p. 396).
   */
  oldestYears: 40,
  /** Table 4 (p. 37): "K 1+ 25808 [22756] (19752 49639)", "MSYR1+ 0.046 [0.048] (0.022 0.064)", "MSYL1+ 0.656 [0.669] (0.532 0.725)". */
  carryingCapacity: 25808,
  msyRate: 0.046,
  msyLevel: 0.656,
  /** "Equation (1) assumes an equal male : female sex ratio at birth" (p. 6); "The sex ratio of calves is 1:1 for the ENP gray whale (Rice and Wolman 1971" (NMFS 2023, the Makah waiver's FEIS, p. 3-78). */
  femaleShare: 0.5,
  /**
   * "F^s_{t,a} = C^s_t / Σ_{a=5}^{x} N^s_{t,a}" (Punt and Wade 2010,
   * Equation 7, p. 7): the catch falls evenly on the whales of five and over;
   * "From 2018 through 2022, Chukotkan hunts averaged 126.4 strikes (SD =
   * 11.7) of gray whales per year" (Scordino et al. 2024, J. Cetacean Res.
   * Manage. 25, p. 12, a fact cited from a paper the game may not carry), the
   * hunt's days spread through the year (its season unread: open); and of the
   * Makah Tribe's: "Currently, hunts are only conducted in Russia" (ibid.).
   */
  huntedFromYears: 5,
  huntPerYear: 126.4,
  /**
   * "Some gray whales in the ENP calve in the shallow, protected lagoons of
   * Baja Mexico ... starting around December 26 and ending approximately at
   * the beginning of March (Swartz and Jones 1983; Sánchez-Pacheco 1998),
   * with a median birthdate around January 27 (Rice and Wolman 1971)" (NMFS
   * 2023, p. 3-78): the calves born evenly from 26 December to 1 March, its
   * median 28 January (Perryman and Lynn's photogrammetry put it earlier:
   * "Based on the proportion of pregnant and recent postpartum females, 15
   * January was estimated to be the median birth date", Rugh et al. 1999,
   * NMFS-AFSC-103, p. 4: open).
   */
  calving: { from: [12, 26], to: [3, 1] },
  /**
   * "the start date of the southward migration for the Granite Canyon study
   * site is fixed at 1 December and the end date at 28 February, where the
   * number of whales passing the watch station on those two dates is assumed
   * to be zero" (Eguchi et al. 2022, NOAA-TM-NMFS-SWFSC-668, p. 2), the
   * station 36.4°N ("Granite Canyon (36º 26' 41" N)", Laake et al. 2009,
   * NOAA-TM-NMFS-AFSC-203, p. 1), near enough the band's 40°N: the whales south of it
   * evenly through the window (NOAA's own curve's parameters are not
   * published), its median 15 January against the seasons' measured 23
   * January in 2022/2023 and 17 January in 2023/2024 (Days 53 and 47,
   * NOAA-TM-NMFS-SWFSC-680 and -695, p. 4).
   */
  southbound: { from: [12, 1], to: [2, 28] },
  /**
   * "Poole (1984) reported the first phase of northbound migrants off the
   * coast of central California from early February to early April"; the
   * "mother-calf pair migrants in the second phase of the northward migration
   * have been sighted passing through the waters off central California from
   * early April to late May (Poole 1984" (NMFS 2023, p. 3-119): each evenly
   * through its window, "early" its month's first ten days and "late" its
   * last, the mothers' median 1 May against Piedras Blancas' measured 19
   * April to 2 May, 2001 to 2010 (Perryman et al. 2011, SC/M11/AWMP3, Table
   * 1, p. 5).
   */
  northbound: { from: [2, 1], to: [4, 10] },
  northboundMothers: { from: [4, 1], to: [5, 31] },
  /**
   * Lang, Eguchi and Weller 2026 (NOAA-TM-NMFS-SWFSC-737, "Abundance of
   * eastern North Pacific gray whales 2025/2026"), Table 1 (p. 8): "2025/2026
   * 17,750 15,930.0 20,530.0", the whales passing Granite Canyon southbound
   * (a calf or two born on the way among them, its season's calving barely
   * begun), the opening's whales of one and over on the calving season's
   * first day; held for the two seasons before it the pregnancy rate looks
   * back on. Its two counts before, "2023/2024 19,600" and "2024/2025
   * 12,890", differ from it by more than the population can change in a
   * year (λmax, 1.062), as NOAA says: "The rates of apparent increase in some
   * recent years (e.g., 2022/2023–2023/2024, 2024/2025– 2025/2026) fall
   * outside the estimated Rmax (0.062) ... Consequently, factors other than
   * demographic events (i.e., births and deaths) must be affecting the
   * estimated abundance" (p. 3). Held, it does not settle the calves: born
   * a whale of one and over, the game's are 0.125, 0.118 and 0.111 in its
   * first three seasons (MEASURED, expected values; NOAA's two counts taken
   * as they are made 2026/2027's 0.156), above any year's birth rate
   * Stewart et al. 2023 estimated ("a high of 0.085 in 1975") and its
   * model's maximum, 0.111 (Science 382, p. 208): open, the largest of the
   * game's gaps with the real whales (`docs/KNOWN_ISSUES.md`, M112).
   */
  count: { season: 2025, whales: 17750 },
};

/** What the model's equations make of the life (`data/graywhales/`): the pregnancy rate at the carrying capacity, and its resilience and compensation. */
export interface GrayWhaleSupply {
  equilibriumRate: number;
  resilience: number;
  compensation: number;
}

/** The classes: 0 to `oldestYears`, the last every whale of that age or older. */
export const grayWhaleClasses = (life: GrayWhaleLife): number => life.oldestYears + 1;

/** The calving season's days in a common year (26 December 2001 to 1 March 2002: 66). */
export function commonSeasonDays(life: GrayWhaleLife): number {
  const { first, last } = windowIn(life.calving, epochDayFromCivil(2001, 12, 1));
  return last - first + 1;
}

const calfDays = new Map<GrayWhaleLife, number>();
/**
 * The share of the calves (class 0) dying a day: a calf's first year, from
 * its birth to its first birthday, holds the calves' survival a year
 * (`calfSurvival`, the model's from one start of the year, its births, to
 * the next). A calf born on the season's day d (0 its first; it dies from
 * the next day) is a calf for 364 - d days of it and a yearling (class 1,
 * from the next 26 December) for d + 1, at every older whale's rate; the
 * rate is the one at which a calf born on each of the season's days alike
 * (a common year's 66; the mature females it is born to fall by 0.48% over
 * them, measured on the table's first season) keeps `calfSurvival` of them
 * to their first birthday on average, by bisection to 1e-15 (M112: at the
 * yearly rate a day, they kept 0.7324, 3.0% more than the model's, their
 * first year's last month or so spent as yearlings).
 */
export function calfDailyDeath(life: GrayWhaleLife): number {
  let x = calfDays.get(life);
  if (x === undefined) {
    const days = commonSeasonDays(life);
    const older = 1 - dailyDeath(life.survival);
    const kept = (daily: number): number => {
      let sum = 0;
      for (let d = 0; d < days; d++) sum += daily ** (YEAR_DAYS - 1 - d) * older ** (d + 1);
      return sum / days;
    };
    let lo = 0;
    let hi = 1;
    while (hi - lo > 1e-15) {
      const mid = (lo + hi) / 2;
      if (kept(mid) < life.calfSurvival) lo = mid;
      else hi = mid;
    }
    x = 1 - (lo + hi) / 2;
    calfDays.set(life, x);
  }
  return x;
}

/**
 * The first of December on or before a day: the migration year's start, the southbound window's first day (the
 * calendar the windows are read in, by its definition, December's in its year and the rest in the next; typed again
 * from `southbound.from`, filed at review M116: a southbound window moved off 1 December must move it too).
 */
function migrationYearStart(epochDay: number): number {
  const c = civilFromEpochDay(epochDay);
  return epochDayFromCivil(c.month === 12 ? c.year : c.year - 1, 12, 1);
}

/** A window's first and last days in the migration year begun on `start` (a 1 December): December's in its year, the other months' in the next. */
function windowIn(span: DayWindow, start: number): { first: number; last: number } {
  const year = civilFromEpochDay(start).year;
  const at = ([month, day]: readonly [number, number]): number => epochDayFromCivil(month === 12 ? year : year + 1, month, day);
  return { first: at(span.from), last: at(span.to) };
}

/** The share of a window's days come by the end of a day of its migration year: 0 before it, 1 after it. */
export function windowShare(span: DayWindow, epochDay: number): number {
  const { first, last } = windowIn(span, migrationYearStart(epochDay));
  return Math.min(1, Math.max(0, (epochDay - first + 1) / (last - first + 1)));
}

/** The eve of the latest calving season begun by the day after a day: the last 25 December on or before it. */
export function calvingEve(life: GrayWhaleLife, epochDay: number): number {
  const start = migrationYearStart(epochDay + 1);
  const first = windowIn(life.calving, start).first;
  return (first <= epochDay + 1 ? first : windowIn(life.calving, migrationYearStart(start - 1)).first) - 1;
}

/** The calving season's day (0 its first) and its days, or null outside it. */
export function calvingDay(life: GrayWhaleLife, epochDay: number): { day: number; days: number } | null {
  const { first, last } = windowIn(life.calving, migrationYearStart(epochDay));
  return epochDay >= first && epochDay <= last ? { day: epochDay - first, days: last - first + 1 } : null;
}

/** The whales of one and over, both sexes. */
export function oneAndOver(w: GrayWhaleState): number {
  let n = 0;
  for (let c = 1; c < w.females.length; c++) n += (w.females[c] as number) + (w.males[c] as number);
  return n;
}

/** The mature females: those that have reached the age at first calving, `matureYears` + 1 and over. */
export function matureFemales(life: GrayWhaleLife, w: GrayWhaleState): number {
  let n = 0;
  for (let c = life.matureYears + 1; c < grayWhaleClasses(life); c++) n += w.females[c] as number;
  return n;
}

/** The pregnancy rate, calves of both sexes a mature female a season, at a population of one and over (Punt and Wade 2010, Equation 3, no catastrophe). */
export function pregnancyRate(life: GrayWhaleLife, supply: GrayWhaleSupply, population: number): number {
  return Math.max(supply.equilibriumRate * (1 + supply.resilience * (1 - (population / life.carryingCapacity) ** supply.compensation)), 0);
}

/**
 * On the calving season's first day, every class a year older, the oldest
 * gathering every whale past its age; the season's pregnancy rate set by the
 * population of two years before (the older of `recent`), and today's whales
 * of one and over put in its place. On any other day, nothing.
 */
function ageGrayWhales(life: GrayWhaleLife, w: GrayWhaleState, epochDay: number, supply: GrayWhaleSupply): void {
  if (calvingDay(life, epochDay)?.day !== 0) return;
  const oldest = grayWhaleClasses(life) - 1;
  for (const counts of [w.females, w.males]) {
    counts[oldest] = (counts[oldest] as number) + (counts[oldest - 1] as number);
    for (let c = oldest - 1; c > 0; c--) counts[c] = counts[c - 1] as number;
    counts[0] = 0;
  }
  // Held, as every stored float is (review M116: the compensation's power reached the save to the last bit).
  w.calvingRate = held(pregnancyRate(life, supply, w.recent[1] as number));
  w.recent = [oneAndOver(w), w.recent[0] as number];
}

/** The hunt a day: the whales it takes from, five and over in the game, and the share of them it strikes. */
export interface GrayWhaleHunt {
  fromYears: number;
  share: number;
}

/**
 * A day of the gray whales' lives, the classes aged on the calving season's
 * first day (`ageGrayWhales`), the day's whales struck returned:
 *
 * 1. **Deaths**, each class's at its rate: the calves' (class 0,
 *    `calfDailyDeath`), every older whale's.
 * 2. **The hunt**: a day's share of the year's strikes, from the whales of
 *    five and over in proportion; or the hunt given (`hunt`: the fit's, a
 *    fixed share of the whales of an age and over, `scripts/graywhale.ts`).
 * 3. **Calves**, through the calving season: each day the season's
 *    pregnancy rate times the mature females, over its days, half of them
 *    female.
 */
export function stepGrayWhales(life: GrayWhaleLife, w: GrayWhaleState, epochDay: number, supply: GrayWhaleSupply, draws: PopulationDraws, hunt?: GrayWhaleHunt): number {
  ageGrayWhales(life, w, epochDay, supply);
  const classes = grayWhaleClasses(life);
  const calf = calfDailyDeath(life);
  const older = dailyDeath(life.survival);
  // 1. Deaths.
  for (const counts of [w.females, w.males]) {
    for (let c = 0; c < classes; c++) {
      const n = counts[c] as number;
      if (n > 0) counts[c] = n - draws.share(n, c === 0 ? calf : older);
    }
  }
  // 2. The hunt.
  const from = hunt?.fromYears ?? life.huntedFromYears;
  let hunted = 0;
  for (let c = from; c < classes; c++) hunted += (w.females[c] as number) + (w.males[c] as number);
  let struck = 0;
  if (hunted > 0) {
    const p = hunt?.share ?? Math.min(1, life.huntPerYear / YEAR_DAYS / hunted);
    for (const counts of [w.females, w.males]) {
      for (let c = from; c < classes; c++) {
        const n = counts[c] as number;
        if (n > 0) {
          const taken = draws.share(n, p);
          counts[c] = n - taken;
          struck += taken;
        }
      }
    }
  }
  // 3. Calves.
  const season = calvingDay(life, epochDay);
  if (season !== null) {
    const calves = (w.calvingRate * matureFemales(life, w)) / season.days;
    w.females[0] = (w.females[0] as number) + draws.count(calves * life.femaleShare);
    w.males[0] = (w.males[0] as number) + draws.count(calves * (1 - life.femaleShare));
  }
  return struck;
}

export function emptyGrayWhales(life: GrayWhaleLife): GrayWhaleState {
  const none = (): number[] => Array<number>(grayWhaleClasses(life)).fill(0);
  return { females: none(), males: none(), recent: [0, 0], calvingRate: 0 };
}

/** What the card reads on a day: the whole population, its females and males, the season's calves, and the whales south and north of 40°N. */
export interface GrayWhaleCounts {
  population: number;
  females: number;
  males: number;
  /** Class 0: the calves born since the calving season began. */
  calves: number;
  south: number;
  north: number;
}

/**
 * The whales south of 40°N on a day: the calves and as many mothers from
 * their birth until the mothers' northbound window takes them, each day
 * evenly through it; every other whale once the southbound window has
 * brought it, until the others' northbound window takes it, each window's
 * share evenly through its days. From June to November, none. From 1 to 25
 * December the calves are last season's, gone north with their mothers in
 * the spring: they come south with the others (M112's review: counted
 * south with their mothers from 1 December, 3,418 whales were south on the
 * first day of a game's December where 210 had come).
 */
export function grayWhaleCounts(life: GrayWhaleLife, w: GrayWhaleState, epochDay: number): GrayWhaleCounts {
  const sum = (xs: readonly number[]): number => xs.reduce((a, b) => a + b, 0);
  const females = sum(w.females);
  const males = sum(w.males);
  const calves = (w.females[0] as number) + (w.males[0] as number);
  // This season's calves and their mothers: from the season's first day until the mothers' window has taken them north
  // (its share 1 from its last day to the next season's first; review M116: a month test copying the windows dropped).
  const pairs = epochDay >= windowIn(life.calving, migrationYearStart(epochDay)).first ? 2 * calves : 0;
  const mothersSouth = pairs * (1 - windowShare(life.northboundMothers, epochDay));
  const others = Math.max(0, females + males - pairs);
  const othersSouth = others * Math.max(0, windowShare(life.southbound, epochDay) - windowShare(life.northbound, epochDay));
  const south = mothersSouth + othersSouth;
  return { population: females + males, females, males, calves, south, north: females + males - south };
}
